import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AppConfig, SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import {
  BookableStorageLocation,
  DistanceUnit,
  IPricing,
  ITimeInterval,
  PRICING_SERVICE,
  PricingService,
  StorageLocationItemBadge,
  TRANSLATE_SERVICE,
} from '@luggagehero/shared/interfaces';
import { SharedDistanceService } from '@luggagehero/shared/services/distance';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { getShopTitleLocation } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, forkJoin, map, Observable, of, Subscription, switchMap } from 'rxjs';

import { BaseComponent } from '../../../core';
import { StorageLocationItemAction } from './storage-location-item.base-component';

const MAX_POI_DISTANCE = 500;

@Component({
  template: '',
  standalone: false,
})
export abstract class SimpleShopItemBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() public bookingRequest = new EventEmitter<void>();
  @Output() public bookShopRequest = new EventEmitter<void>();
  public averageRating = 0;
  public numberOfReviews = 0;
  public showNumberOfReviews = true;

  public actions: StorageLocationItemAction[];
  public badges: StorageLocationItemBadge[] = [];

  protected pricingService = inject<PricingService>(PRICING_SERVICE);
  private storageLocationService = inject(SharedShopsService);
  private distanceService = inject(SharedDistanceService);
  private featureResolver = inject(FeatureResolver);
  private appSettings = inject(SharedAppSettingsService);
  private criteria = inject(SharedStorageCriteriaService);
  private translate = inject(TranslateService);
  private translateService = inject(TRANSLATE_SERVICE);
  private cd = inject(ChangeDetectorRef);

  private _shop: BookableStorageLocation;
  private _distanceUnit: DistanceUnit = 'metric';
  private subscriptions: Subscription[] = [];
  private _index = -1;
  private isDailyPricingByDefault = true;

  public get shop(): BookableStorageLocation {
    return this._shop;
  }
  @Input() set shop(value: BookableStorageLocation) {
    this._shop = value;

    this.numberOfReviews = this.storageLocationService.getNumberOfRatings(value);
    this.showNumberOfReviews = this.numberOfReviews > 1;

    this.averageRating = value?.stats?.averageRating;

    this.badges = this.storageLocationService.getBadgeList(value);
  }

  public get distanceUnit(): DistanceUnit {
    return this._distanceUnit;
  }
  @Input() public set distanceUnit(value: DistanceUnit) {
    this._distanceUnit = value;
    this.cd.markForCheck();
  }

  public get locationInfo(): Observable<string> {
    return forkJoin([
      this.distanceService.convertDistance(this.shop.distance, this.distanceUnit, false),
      this.translate.get('DISTANCE_AWAY'),
    ]).pipe(map(([distance, suffix]) => `${distance} ${suffix}`));
  }

  public get locationType(): string {
    if (!AppConfig.IS_STORAGE_LOCATION_TYPE_ENABLED) {
      return null;
    }

    let locationType: string;

    // TODO: Parse locationType to translation key
    switch (this.shop.locationType) {
      default:
        locationType = 'LUGGAGE_STORAGE';
        break;
    }

    return locationType;
  }

  public get title(): string {
    if (!this.shop) {
      return '';
    }

    // AM-4777: shares the chain with the shop card and the details page, so the native shop list
    // and both map overlay cards name a curated shop the same way its Google listing does. Same
    // 500m POI limit as before — this is the other call site that had one.
    const location = getShopTitleLocation(this.shop, { maxPoiDistance: MAX_POI_DISTANCE });
    const prefix = AppConfig.IS_LUGGAGEHERO_BRAND_IN_SHOP_TITLE_ENABLED
      ? 'LuggageHero'
      : (this.translate.instant('LUGGAGE_STORAGE') as string);

    return location ? `${prefix} ${location}` : prefix;
  }

  public get pricing(): IPricing {
    if (!this.shop) {
      return null;
    }
    return this.shop.pricing;
  }

  public get openingHours(): ITimeInterval[] {
    return this.storageLocationService.getOpeningHoursForDate(this.criteria.current.period.from, this.shop);
  }

  public get nextOpenDay(): Date {
    return this.storageLocationService.getNextOpenDay(this.criteria.current.period.from, this.shop);
  }

  public get opensAt(): Observable<string> {
    return this.storageLocationService.getOpensAt(this.criteria.current.period.from, this.shop);
  }

  public get closesAt(): Observable<string> {
    return this.storageLocationService.getClosesAt(this.criteria.current.period.from, this.shop);
  }

  public get isMultiDayStorageAllowed(): boolean {
    return this.storageLocationService.isMultidayStorageAllowed(this.shop);
  }

  public get isRecommended(): boolean {
    return this.storageLocationService.isRecommended(this.shop);
  }

  public get isOpenLate(): boolean {
    if (!AppConfig.IS_OPEN_LATE_BADGE_ON_SIMPLE_SHOP_ITEM_ENABLED) {
      return false;
    }
    return this.storageLocationService.isOpenLate(this.criteria.period.from, this.shop);
  }

  public get isAlwaysOpen(): boolean {
    if (!AppConfig.IS_ALWAYS_OPEN_BADGE_ON_SIMPLE_SHOP_ITEM_ENABLED) {
      return false;
    }
    return this.storageLocationService.isAlwaysOpen(this.criteria.period.from, this.criteria.period.to, this.shop);
  }

  public get isHighlyRated(): boolean {
    if (!AppConfig.IS_HIGHLY_RATED_BADGE_ON_SIMPLE_SHOP_ITEM_ENABLED) {
      return false;
    }
    if (this.isRecommended) {
      // Don't show highly rated badge if shop is recommended
      return false;
    }
    return this.storageLocationService.isHighlyRated(this.shop);
  }

  public get isFullyBooked(): boolean {
    return this.isOpen && !this.shop.available;
  }

  public get isOpen(): boolean {
    return this.storageLocationService.isOpen(this.criteria.period.from, this.shop);
  }

  public get isClosed(): boolean {
    return this.storageLocationService.isClosed(this.criteria.period.from, this.shop);
  }

  public get hourlyRateHtml(): Observable<string> {
    return this.pricingService.transformPricing(this.pricing, false, false, true, false, false, true).pipe(
      map((html) => html?.replace('>/', '>') ?? ''), // HACK: Remove "/" from "/hour"
    );
  }

  public get dailyRateHtml(): Observable<string> {
    return this.pricingService.transformPricing(this.pricing, false, true, false, false, false, true).pipe(
      map((html) => html?.replace('>/', '>') ?? ''), // HACK: Remove "/" from "/day"
    );
  }

  public get footNotePart1Html(): Observable<string> {
    return combineLatest([this.dailyRateHtml, this.hourlyRateHtml]).pipe(
      map(([dailyHtml, hourlyHtml]) => (this.isDailyPricingByDefault ? dailyHtml : hourlyHtml)),
      switchMap((html) => this.asFromPrice(html)),
    );
  }

  public get footNotePart2Html(): Observable<string> {
    return combineLatest([this.dailyRateHtml, this.hourlyRateHtml]).pipe(
      map(([dailyHtml, hourlyHtml]) => (this.isDailyPricingByDefault ? hourlyHtml : dailyHtml)),
    );
  }

  /**
   * The card shows the cheapest prices a shop offers (booked ahead), so they read as "from" prices
   * (AM-5114): booking for today, or walking in, can cost more. One "from" covers the pair, so only the
   * first part carries it: "from kr 10 hour / kr 45 day". The translation places the word, and the
   * bare price stays when it hasn't loaded rather than a raw key.
   */
  private asFromPrice(priceHtml: string): Observable<string> {
    if (!priceHtml) {
      return of(priceHtml);
    }
    return this.translate
      .get('PRICE_FROM', { price: priceHtml })
      .pipe(map((text: string) => (text && text !== 'PRICE_FROM' ? text : priceHtml)));
  }

  public get serviceFeeFootnote(): string {
    // Gated by app settings (global switch or per-city allowlist); off by default.
    if (!this.featureResolver.showServiceFeeFootnote(this.shop)) {
      return '';
    }
    // Sourced from the shop's productList (same pricing as issued orders),
    // not the legacy `IPricing.serviceFee`. Empty until the product list is
    // available; the `listShopsAsyncComplete` subscription below re-renders
    // the card once the second list-shops response arrives.
    const serviceFee = this.storageLocationService.getServiceFeeForShop(this.shop);
    if (!serviceFee) {
      return '';
    }
    return this.translateService.instant('PLUS_SERVICE_FEE', undefined, {
      fee: this.pricingService.format(serviceFee.cost, serviceFee.currency),
    });
  }

  public get holidayNoticeHtml(): string {
    return this.storageLocationService.getHolidayNoticeHtml(this.criteria.period.from, this.shop);
  }

  public ngOnInit(): void {
    this.actions = [
      {
        callToAction: 'BOOK_NOW',
        isPrimary: true,
        onClick: (event) => this.requestBooking(event),
      },
    ];

    if (!this.appSettings.current?.IS_SHOP_WALK_IN_CTA_DISABLED) {
      this.actions.unshift({
        callToAction: 'Drop off',
        isPrimary: true,
        onClick: (event) => this.openBookShop(event),
      });
    }

    void firstValueFrom(this.featureResolver.isDailyPricingDefault$).then(
      (enabled) => (this.isDailyPricingByDefault = enabled),
    );

    this.subscriptions.push(
      this.storageLocationService.listShopsAsyncComplete.subscribe(() => {
        // The second list-shops response backfills `pricing` and `activePrograms`
        // onto the shop reference. Re-derive the badges so the honeymoon
        // discount percentage shows up once it's available.
        if (this._shop) {
          this.badges = this.storageLocationService.getBadgeList(this._shop);
        }
        this.cd.markForCheck();
      }),
    );
  }

  public ngOnDestroy(): void {
    try {
      this.subscriptions?.forEach((s) => s.unsubscribe());
    } catch {
      // Ignore
    }
  }

  public requestBooking(e: Event) {
    e.stopPropagation();
    e.preventDefault();
    this.bookingRequest.emit();
  }

  public openBookShop(e: Event) {
    e.stopPropagation();
    e.preventDefault();
    this.bookShopRequest.emit();
  }

  get index(): number {
    return this._index;
  }
  @Input() set index(value: number) {
    this._index = value;
    this.cd.markForCheck();
  }
}
